import React, { useEffect, useState } from 'react';
import {
  Button,
  ButtonColor,
  ButtonSize,
  IconType,
  OptionSelectionButtonLayoutProps,
  usePopupManager,
  SelectableOption, SelectableOptionsGroup, isListOfSelectableOptionsGroups,
} from 'ui-builder';
import { Arrow, useHover, useLayer } from 'react-laag';
import styles from './OptionSelectionButtonLayout.module.scss';
import { Separator } from "features/nekst-widgets";
import useFeatureToggleService from 'feature-toggle';
import ShadcnOptionSelectionButtonLayout from './ShadcnOptionSelectionButtonLayout';

function OptionsList(props: {
  options: SelectableOption<string>[]
}) {
  return (
    <>
      {props.options.map((option) => {
        if (option) {
          return (
            <button
              key={`option-${option?.value}`}
              type="button"
              onClick={() => option.onSelected()}
              disabled={option.disabled}
              title={option.title}
            >
              {option.label}
            </button>
          );
        } else {
          return null;
        }
      })}
    </>
  );
}

function OptionsGroup(props: {
  group: SelectableOptionsGroup<string | number>
}) {
  const options = props.group.options;

  const notEmptyOptions = options.filter((option) => option !== null && option !== false);

  if (notEmptyOptions.length > 0) {
    return (
      <div>
        {props.group.title && <div className={styles.groupName}>{props.group.title}</div>}
        <OptionsList options={notEmptyOptions as SelectableOption<string>[]} />
      </div>
    );
  } else {
    return null;
  }
}

function OptionsGroups(props: {
  groups: SelectableOptionsGroup<string | number>[]
}) {
  return (
    <>
      {props.groups.map((group, index) => {
        return (
          <React.Fragment key={`group-${index}`}>
            <OptionsGroup group={group} />
            {index < props.groups.length - 1 ? <Separator className={styles.separator} /> : null}
          </React.Fragment>
        );
      })}
    </>
  );
}


function LegacyOptionSelectionButtonLayout(
  props: OptionSelectionButtonLayoutProps,
) {
  const [isOpen, hoverProps] = useHover(
    {
      delayLeave: 50,
      hideOnScroll: true,
    },
  );

  const [isInPopup, setInPopup] = useState<boolean>(false);
  const popupManager = usePopupManager();

  const {
    renderLayer,
    triggerProps,
    layerProps,
    arrowProps,
  } = useLayer({
    isOpen: isOpen && !props.isDisabled,
    placement: 'bottom-end',
    overflowContainer: false,
    triggerOffset: 10,
    containerOffset: 12,
    auto: true,
    possiblePlacements: [
      'bottom-start',
      'bottom-end',
      'bottom-center',
      'top-start',
      'top-center',
      'top-end',
    ],
  });

  const size = props.size || ButtonSize.BIG;

  useEffect(() => {
    setInPopup(popupManager.isOpen());
  }, []);

  /* eslint-disable react/jsx-props-no-spreading */
  return (
    <div
      className={`${styles.dropdown} ${size}`}
      {...(props.isDisabled ? {} : hoverProps)}
    >
      <Button
        text={`${props.text}`}
        size={size}
        isFilled
        color={ButtonColor.BLUE}
        iconType={IconType.ARROW_DOWN}
        elementRef={triggerProps.ref}
        testId={props.testId}
        isDisabled={props.isDisabled}
        title={props.title}
      />
      {isOpen && renderLayer(
        <div
          {...layerProps}
          className={`${styles.contentWrapper} ${isInPopup ? styles.inPopup : ''}`}
        >
          <div
            className={styles.content}
          >
            {isListOfSelectableOptionsGroups(props.options) && <OptionsGroups groups={props.options as SelectableOptionsGroup<string | number>[]} /> }
            {!isListOfSelectableOptionsGroups(props.options) && <OptionsList options={props.options as SelectableOption<string>[]} />}
          </div>
          <Arrow onPointerEnterCapture={undefined}
                 onPointerLeaveCapture={undefined}
                 {...arrowProps}
          />
        </div>,
      )}
    </div>
  );
}

export default function OptionSelectionButtonLayout(
  props: OptionSelectionButtonLayoutProps,
) {
  const { isShadcnUiEnabled } = useFeatureToggleService();

  if (isShadcnUiEnabled()) {
    return <ShadcnOptionSelectionButtonLayout {...props} />;
  } else {
    return <LegacyOptionSelectionButtonLayout {...props} />;
  }
}
